import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow, { BodyTableRow } from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { clearState } from 'utils/storage';
import { useOrderFeedSettingsState } from 'views/order-feed/context/order-feed-settings-context';
import { postOrder } from 'views/order-feed/features/my-supply-plan/api/orders-api';
import { RecommendedOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/types/recommendation.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { CreateOrderInput } from 'views/order-feed/features/my-supply-plan/types/create-order-input.type';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { buildCreateOrderInput } from 'views/order-feed/features/my-supply-plan/utils/create-order-utils';
import {
  formatDate,
  getSelectedTransportCompany,
  getSiteTransportCompanies,
} from 'views/order-feed/features/my-supply-plan/utils/order-utils';

type Props = {
  recommendedOrder: RecommendedOrder;
  listOptions: HeadCell[];
};

const PAGE_NAME_ID = 'orderFeed.listOrderSupplyPlan';

const ChileRecommendedOrderListRow: React.FC<Props> = ({ recommendedOrder, listOptions }) => {
  const intl = useIntl();
  const params = useParams<MySupplyPlanParams>();
  const { account } = useAccountState();
  const {
    orderSettingsQuery: { data: orderSettings },
  } = useOrderFeedSettingsState();
  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();
  const { createOrderFeedEvent } = useGoogleTagManager();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const deliveryMethods = getSiteTransportCompanies(account.company?.addressAccess, params.globalSiteId) ?? [];

  const { handleSubmit, control, setValue } = useForm<CreateOrderFormValues>({
    defaultValues: {
      customerOrderReference: '',
      transportMethod: '',
      addressId: '',
      requestedDispatchDate: '',
      siteErpId: params.siteErpId,
      orderLines: [{ itemNumber: '', orderQuantity: 0 }],
      orderComment: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const onCreateSuccess = (orderNumber: string) => {
    snackbarDispatch({
      type: SnackbarActionTypes.SUCCESS_SNACKBAR,
      payload: { msg: intl.formatMessage({ id: `${PAGE_NAME_ID}.OrderList.CreateOrder.OrderCreated` }) },
    });
    clearState({ key: 'createOrderData' });
    queryClient.invalidateQueries({ queryKey: ['getOrders', account.company!.customerErpId, params.siteErpId] });
    dialogRef.current?.setOpen(false);
    createOrderFeedEvent('create', { orderNumber, supplyPlanType: SupplyPlanType.OFD1 });
  };

  const createRecommendedOrder = usePost<string, { data: CreateOrderInput }>(
    (request) => postOrder(account.company!.customerErpId, request.data).then((response) => response.orderNumber),
    onCreateSuccess,
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error },
      });
    }
  );

  const handleCreateRecommendedOrder = (data: CreateOrderFormValues) => {
    const selectedTransportCompany = getSelectedTransportCompany(deliveryMethods, data.transportMethod);

    createRecommendedOrder.mutate({
      data: buildCreateOrderInput({
        ...data,
        transportMethod: selectedTransportCompany?.key ?? '',
        addressId: selectedTransportCompany?.name ?? '',
        requestedDispatchDate: formatDate(recommendedOrder.date || recommendedOrder.arrivalDate),
        siteErpId: recommendedOrder.siteErpId,
        orderLines: recommendedOrder.recommendedOrderLines.map((orderLine) => ({
          itemNumber: orderLine.productNumber,
          orderQuantity: orderLine.recommendedQuantity,
        })),
      }),
    });
  };

  return (
    <ListRow
      options={listOptions}
      body={{
        options: [
          { id: 'status', colSpan: 1, autoWidth: true, label: '' },
          { id: 'productLabel', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.productLabel` }) },
          { id: 'recommendedQuantity', align: 'right', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.amountLabel` }) },
        ],
        children: (
          <>
            {recommendedOrder.recommendedOrderLines.map((orderLine, lineIndex) => (
              <BodyTableRow key={`${recommendedOrder.date}_${recommendedOrder.scheduleId}_${orderLine.productNumber}_${lineIndex}`}>
                <ListCell size="small" colSpan={1} borderSize={0}></ListCell>
                <ListCell>{orderLine.productName}</ListCell>
                <ListCell align="right">
                  <FormattedNumber
                    value={orderLine.recommendedQuantity}
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                    style="unit"
                    unit="kilogram"
                  />
                </ListCell>
              </BodyTableRow>
            ))}
          </>
        ),
        span: listOptions.length,
      }}>
      <ListCell align="center" />
      <ListCell>
        <FormattedMessage id={`${PAGE_NAME_ID}.orderList.recommendation`} />
      </ListCell>
      <ListCell />
      <ListCell />
      <ListCell>
        <FormattedDate value={recommendedOrder.arrivalDate} day="2-digit" month="short" year="numeric" />
      </ListCell>
      <ListCell align="right">
        <FormattedNumber
          value={recommendedOrder.recommendedOrderLines.reduce((total, line) => total + line.recommendedQuantity, 0)}
          style="unit"
          unit="kilogram"
        />
      </ListCell>
      <ListCell align="right" sx={{ width: { xs: '100%', md: 'auto' } }}>
        {account.company?.features.hasOrderFeed && (
          <Dialog
            ref={dialogRef}
            maxWidth="xs"
            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.createOrderDialog.recommendedTitle` })}
            button={
              <Button size="small" disabled={orderSettings?.isUnderMaintenance} sx={{ ml: 1 }}>
                <FormattedMessage id="general.confirm" />
              </Button>
            }>
            <form onSubmit={handleSubmit(handleCreateRecommendedOrder)}>
              {createRecommendedOrder.isPending && <LoadingSpinner overlay />}
              <FlexBox>
                {deliveryMethods.length !== 1 && (
                  <Select
                    dark
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createOrderDialog.methodOfDeliveryLabel` })}
                    controller={{
                      name: 'transportMethod',
                      control,
                      rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                    }}
                    onChange={(event) => {
                      const selectedTransportCompany = getSelectedTransportCompany(deliveryMethods, String(event.target.value));
                      setValue('addressId', selectedTransportCompany?.name ?? '');
                    }}>
                    {deliveryMethods.map((transport) => (
                      <RadioSelectItem key={transport.key} value={transport.key}>
                        {transport.key} - {transport.name}
                      </RadioSelectItem>
                    ))}
                  </Select>
                )}
                <TextField
                  dark
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderList.createOrder.customerReference` })}
                  maxLength={20}
                  controller={{ name: 'customerOrderReference', control }}
                />
                <TextField
                  dark
                  multiline
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createOrderDialog.orderCommentLabel` })}
                  controller={{ name: 'orderComment', control }}
                />
                <Box display="flex" justifyContent="flex-end">
                  <Button size="small" type="submit" disabled={orderSettings?.isUnderMaintenance}>
                    <FormattedMessage id="general.confirm" />
                  </Button>
                </Box>
              </FlexBox>
            </form>
          </Dialog>
        )}
      </ListCell>
    </ListRow>
  );
};

export default ChileRecommendedOrderListRow;
